import cx from 'classnames/bind';
import DeleteRegular from 'mini-fluent-svg-icons/lib/icons/DeleteRegular';
import FlagRegular from 'mini-fluent-svg-icons/lib/icons/FlagRegular';
import FolderArrowRightRegular from 'mini-fluent-svg-icons/lib/icons/FolderArrowRightRegular';
import getIsJunkValueToSet from 'mini-mail-triage-actions/lib/utils/markAsJunk/getIsJunkValueToSet';
import getSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import isItemView from 'mini-mail-list-store/lib/utils/isItemView';
import MailProhibitedRegular from 'mini-fluent-svg-icons/lib/icons/MailProhibitedRegular';
import MailReadRegular from 'mini-fluent-svg-icons/lib/icons/MailReadRegular';
import MailUnreadRegular from 'mini-fluent-svg-icons/lib/icons/MailUnreadRegular';
import React from 'react';
import strings from 'owa-strings/owa-mini-strings';
import styles from './MailMultiSelectFooter.scss';
import { IconButton } from 'mini-fluent-svg-icons/lib/components/IconButton';
import { isInSearchView } from 'mini-mail-list-store/lib/selectors/isInSearchView';
import { type MultiSelectTriageAction } from 'mini-mail-triage-actions/lib/schema/MultiSelectTriageAction';
import { observer } from 'mobx-react-lite';
import { setShowMoveToFolderPane } from 'mini-mail-folder-store/lib/actions/setShowMoveToFolderPane';
import { signalWorkflowEvent } from 'mini-workflow-manager';
import { useRegisterWorkflowActions } from 'mini-workflow-tracking/lib/utils/useRegisterWorkflowActions';
import {
    getIsAnySelectedUnflagged,
    getIsAnySelectedUnread,
} from 'mini-mail-list-store/lib/utils/getTriageState';
import {
    lazyDeleteSelectedConversations,
    lazyApplySelectedConversationsFlaggedState,
    lazyMarkSelectedConversationsJunkNotJunk,
    lazyDeleteSelectedItems,
    lazyMarkSelectedItemsJunkNotJunk,
    lazyApplySelectedItemsFlaggedState,
    lazyMarkSelectedItemsReadState,
    lazyMarkSelectedConversationsReadState,
} from 'mini-mail-triage-actions';
import {
    getMarkAsReadAriaLabel,
    getMarkAsFlaggedAriaLabel,
    getMarkAsJunkAriaLabel,
    getDeleteAriaLabel,
    getMoveToFolderAriaLabel,
} from '../utils/getMultiSelectFooterAriaLabels';
import {
    ACTION_DELETE_SELECTED_ITEMS,
    ACTION_DELETE_SELECTED_CONVERSATIONS,
} from 'mini-mail-triage-actions/lib/actions/constants/ListViewActionNames';
import {
    DELETE_SELECTED_MAIL_ITEMS_BEGIN,
    DELETE_SELECTED_CONVERSATIONS_BEGIN,
    SELECT_ALL_MARK_READ,
    SELECT_ALL_DELETE,
} from 'mini-workflow-tracking/lib/constants/ListViewWorkflowConstants';

const classNames = cx.bind(styles);

const logSelectAll = (eventName: string) => {
    const tableView = getSelectedTableView();
    if (
        tableView.isInVirtualSelectAllMode ||
        tableView.itemIds.length === tableView.selectedItemsIds.size
    ) {
        signalWorkflowEvent(eventName, { nsa: true });
    }
};

const onMarkSelectedAsRead = async () => {
    logSelectAll(SELECT_ALL_MARK_READ);
    return isItemView()
        ? await lazyMarkSelectedItemsReadState()
        : await lazyMarkSelectedConversationsReadState();
};

const onFlagSelected = () =>
    isItemView()
        ? lazyApplySelectedItemsFlaggedState()
        : lazyApplySelectedConversationsFlaggedState();

const onMoveToFolderSelected = () => {
    setShowMoveToFolderPane(true);
};

const onDeleteSelected = () => {
    logSelectAll(SELECT_ALL_DELETE);
    return isItemView() ? lazyDeleteSelectedItems() : lazyDeleteSelectedConversations();
};

const onMarkAsJunkSelected = async () =>
    isItemView()
        ? lazyMarkSelectedItemsJunkNotJunk()
        : await lazyMarkSelectedConversationsJunkNotJunk();

interface MailMultiSelectFooterProps {
    onMultiSelectAction?: (actionName: MultiSelectTriageAction) => void;
}

export const MailMultiSelectFooter = observer((props: MailMultiSelectFooterProps) => {
    const { onMultiSelectAction } = props ?? {};
    const isInVirtualSelectAllMode = getSelectedTableView().isInVirtualSelectAllMode;

    const onMultiSelectClick =
        (actionName: MultiSelectTriageAction, multiSelectCallback: () => void) => () => {
            onMultiSelectAction?.(actionName);
            multiSelectCallback();
        };

    const leftIcons = [
        {
            // Read/Unread
            icon: getIsAnySelectedUnread() ? MailReadRegular : MailUnreadRegular,
            onClick: onMultiSelectClick(1, onMarkSelectedAsRead),
            ariaLabel: getMarkAsReadAriaLabel(),
            label: getIsAnySelectedUnread() ? strings.readText : strings.unreadText,
            iconStyle: styles.multiSelectFooterIcon,
        },
        !isInVirtualSelectAllMode && {
            // Flag/Unflag
            icon: FlagRegular,
            onClick: onMultiSelectClick(2, onFlagSelected),
            ariaLabel: getMarkAsFlaggedAriaLabel(),
            label: getIsAnySelectedUnflagged() ? strings.flagText : strings.unflagText,
            iconStyle: styles.multiSelectFooterIcon,
        },
        !isInVirtualSelectAllMode && {
            // Move to folder
            icon: FolderArrowRightRegular,
            onClick: onMultiSelectClick(3, onMoveToFolderSelected),
            ariaLabel: getMoveToFolderAriaLabel(),
            label: strings.moveText,
            iconStyle: styles.multiSelectFooterIcon,
        },
        {
            // Delete
            icon: DeleteRegular,
            onClick: onMultiSelectClick(4, onDeleteSelected),
            ariaLabel: getDeleteAriaLabel(),
            label: strings.deleteText,
            iconStyle: styles.multiSelectFooterIcon,
        },
        getIsJunkValueToSet() &&
            !isInVirtualSelectAllMode && {
                // Mark as Junk
                icon: MailProhibitedRegular,
                onClick: onMultiSelectClick(5, onMarkAsJunkSelected),
                ariaLabel: getMarkAsJunkAriaLabel(),
                label: strings.junkText,
                iconStyle: styles.multiSelectFooterIcon,
            },
    ].filter(Boolean);

    useRegisterWorkflowActions({
        [ACTION_DELETE_SELECTED_ITEMS]: DELETE_SELECTED_MAIL_ITEMS_BEGIN,
        [ACTION_DELETE_SELECTED_CONVERSATIONS]: DELETE_SELECTED_CONVERSATIONS_BEGIN,
    });

    return (
        <div
            className={classNames(styles.multiSelectFooter, {
                [styles.multiSelectFooterLandscape]: !isInSearchView(),
            })}
            aria-label={strings.multiSelectActionsFooterAriaLabel}>
            {leftIcons.map(icon => (
                <IconButton
                    icon={icon.icon}
                    onClick={icon.onClick}
                    ariaLabel={icon.ariaLabel}
                    className={cx(icon.iconStyle, styles.multiSelectFooterButton)}
                    label={icon.label}
                />
            ))}
        </div>
    );
});
